import { supabase } from "@/integrations/supabase/client";
import { DbTask } from "../tasks/types";

/**
 * Interface for task context sent to AI
 */
export interface TaskContext {
  activeTasks: Array<{
    title: string;
    description: string;
    status: 'active';
  }>;
  completedTasks: Array<{
    title: string;
    description: string;
    status: 'completed';
  }>;
}

/**
 * Fetch tasks for a goal that haven't been sent as context yet
 */
export async function getUnsentTaskContextForGoal(goalId: string, userId: string): Promise<TaskContext | null> {
  console.log(`[TaskContext] Fetching unsent task context for goal ${goalId}`);
  
  // Fetch all tasks for this goal that haven't been sent as context
  // Note: RLS policies handle access control (owned + collaborative access)
  const { data: tasks, error } = await supabase
    .from('planner_tasks')
    .select('*')
    .eq('goal_id', goalId)
    .eq('suggested_task_context_sent', false)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[TaskContext] Error fetching unsent tasks:', error);
    return null;
  }

  if (!tasks || tasks.length === 0) {
    console.log('[TaskContext] No unsent tasks found for goal');
    return null;
  }

  // Separate active and completed tasks
  const activeTasks = tasks
    .filter(task => !task.is_completed)
    .map(task => ({
      title: task.title,
      description: task.description || '',
      status: 'active' as const
    }));

  const completedTasks = tasks
    .filter(task => task.is_completed)
    .map(task => ({
      title: task.title,
      description: task.description || '',
      status: 'completed' as const
    }));

  const context: TaskContext = {
    activeTasks,
    completedTasks
  };

  console.log(`[TaskContext] Found unsent context:`, {
    active: activeTasks.length,
    completed: completedTasks.length,
    total: tasks.length
  });

  return context;
}

/**
 * Mark tasks as having their context sent to AI
 */
export async function markTasksAsContextSent(goalId: string, userId: string): Promise<void> {
  console.log(`[TaskContext] Marking tasks as context sent for goal ${goalId}`);
  
  const { error } = await supabase
    .from('planner_tasks')
    .update({ suggested_task_context_sent: true } as any)
    .eq('goal_id', goalId)
    .eq('user_id', userId)
    .eq('suggested_task_context_sent', false);

  if (error) {
    console.error('[TaskContext] Error marking tasks as context sent:', error);
    throw new Error('Failed to mark tasks as context sent');
  }

  console.log('[TaskContext] ✅ Successfully marked tasks as context sent');
}

/**
 * Reset context_sent flag when a task is completed
 * This allows us to inform AI about newly completed tasks
 */
export async function resetTaskContextOnCompletion(taskId: string): Promise<void> {
  console.log(`[TaskContext] Resetting context sent flag for completed task ${taskId}`);
  
  const { error } = await supabase
    .from('planner_tasks')
    .update({ suggested_task_context_sent: false } as any)
    .eq('id', taskId);

  if (error) {
    console.error('[TaskContext] Error resetting context sent flag:', error);
    throw new Error('Failed to reset context sent flag');
  }

  console.log('[TaskContext] ✅ Reset context sent flag for completed task');
}

/**
 * Fetch tasks for a goal that haven't been sent as research context yet
 */
export async function getUnsentResearchContextForGoal(goalId: string, userId: string): Promise<TaskContext | null> {
  console.log(`[ResearchContext] Fetching unsent research context for goal ${goalId}`);
  
  // Fetch all tasks for this goal that haven't been sent as research context
  // Note: RLS policies handle access control (owned + collaborative access)
  const { data: tasks, error } = await supabase
    .from('planner_tasks')
    .select('*')
    .eq('goal_id', goalId)
    .eq('suggested_research_context_sent', false)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[ResearchContext] Error fetching unsent tasks:', error);
    return null;
  }

  if (!tasks || tasks.length === 0) {
    console.log('[ResearchContext] No unsent tasks found for goal');
    return null;
  }

  // Separate active and completed tasks
  const activeTasks = tasks
    .filter(task => !task.is_completed)
    .map(task => ({
      title: task.title,
      description: task.description || '',
      status: 'active' as const
    }));

  const completedTasks = tasks
    .filter(task => task.is_completed)
    .map(task => ({
      title: task.title,
      description: task.description || '',
      status: 'completed' as const
    }));

  const context: TaskContext = {
    activeTasks,
    completedTasks
  };

  console.log(`[ResearchContext] Found unsent context:`, {
    active: activeTasks.length,
    completed: completedTasks.length,
    total: tasks.length
  });

  return context;
}

/**
 * Mark tasks as having their context sent to AI for research generation
 */
export async function markTasksAsResearchContextSent(goalId: string, userId: string): Promise<void> {
  console.log(`[ResearchContext] Marking tasks as research context sent for goal ${goalId}`);
  
  const { error } = await supabase
    .from('planner_tasks')
    .update({ suggested_research_context_sent: true } as any)
    .eq('goal_id', goalId)
    .eq('user_id', userId)
    .eq('suggested_research_context_sent', false);

  if (error) {
    console.error('[ResearchContext] Error marking tasks as research context sent:', error);
    throw new Error('Failed to mark tasks as research context sent');
  }

  console.log('[ResearchContext] ✅ Successfully marked tasks as research context sent');
}

/**
 * Reset research context sent flag when a task is completed
 * This allows us to inform AI about newly completed tasks for research
 */
export async function resetResearchContextOnCompletion(taskId: string): Promise<void> {
  console.log(`[ResearchContext] Resetting research context sent flag for completed task ${taskId}`);
  
  const { error } = await supabase
    .from('planner_tasks')
    .update({ suggested_research_context_sent: false } as any)
    .eq('id', taskId);

  if (error) {
    console.error('[ResearchContext] Error resetting research context sent flag:', error);
    throw new Error('Failed to reset research context sent flag');
  }

  console.log('[ResearchContext] ✅ Reset research context sent flag for completed task');
}

/**
 * Build context message for AI based on user's tasks for research generation
 */
export function buildResearchContextMessage(context: TaskContext): string {
  const parts: string[] = [];
  
  if (context.activeTasks.length > 0) {
    parts.push(`Tasks the user is currently working on (provide research that supports and enhances these activities):`);
    context.activeTasks.forEach((task, index) => {
      parts.push(`${index + 1}. "${task.title}" - ${task.description}`);
    });
  }
  
  if (context.completedTasks.length > 0) {
    parts.push(`\nTasks the user has already completed (provide advanced research for the next phase beyond these accomplishments):`);
    context.completedTasks.forEach((task, index) => {
      parts.push(`${index + 1}. ✅ "${task.title}" - ${task.description}`);
    });
  }
  
  if (parts.length === 0) {
    return '';
  }
  
  return `\n\nUser Context:\n${parts.join('\n')}\n\nIMPORTANT: For active tasks, suggest research that deepens understanding and improves execution. For completed tasks, suggest research that explores advanced topics and next-level strategies beyond what's been accomplished.`;
}

/**
 * Build context message for AI based on user's tasks
 */
export function buildTaskContextMessage(context: TaskContext): string {
  const parts: string[] = [];
  
  if (context.activeTasks.length > 0) {
    parts.push(`Tasks the user is currently working on (expand this scope - suggest complementary tasks that work alongside these):`);
    context.activeTasks.forEach((task, index) => {
      parts.push(`${index + 1}. "${task.title}" - ${task.description}`);
    });
  }
  
  if (context.completedTasks.length > 0) {
    parts.push(`\nTasks the user has already completed (move beyond this scope - suggest next-level tasks that build on these achievements):`);
    context.completedTasks.forEach((task, index) => {
      parts.push(`${index + 1}. ✅ "${task.title}" - ${task.description}`);
    });
  }
  
  if (parts.length === 0) {
    return '';
  }
  
  return `\n\nUser Context:\n${parts.join('\n')}\n\nIMPORTANT: For active tasks, suggest complementary work that expands the current scope. For completed tasks, suggest advanced next steps that go beyond what's already done. Never duplicate or suggest variations of existing tasks.`;
} 